// Sprint PKG-VIZ D1 (#801) — confirm/reject pills on AI-inferred edges.
//
// A1's <PKGCanvas /> registers no `edgeTypes` at all — every edge renders
// as React Flow's default type, styled only via `style`/`markerEnd`
// (edgeStyle.ts). This is the first custom edge type in that registry,
// the natural parallel to the existing `nodeTypes` registry (nodes/).
//
// Built on `BaseEdge` + `EdgeLabelRenderer` deliberately, over a manually
// absolutely-positioned overlay `div`: EdgeLabelRenderer pins its content
// to the edge midpoint via React Flow's own pan/zoom transform, so the
// pills stay correctly placed under every canvas interaction for free. A
// manual overlay would have to re-implement that transform math and
// would drift on pan/zoom — a defect invisible in a static test and only
// visible in the browser (paste-back gate V3).

import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from '@xyflow/react';
import type { PKGEdge } from '@blueprint/shared';

export interface PKGEdgeWithActionsData extends Record<string, unknown> {
  domainEdge: PKGEdge;
  onConfirm?: (edge: PKGEdge) => void;
  onReject?: (edge: PKGEdge) => void;
}

type PKGEdgeWithActionsProps = EdgeProps & {
  data?: PKGEdgeWithActionsData;
};

export function PKGEdgeWithActions({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
}: PKGEdgeWithActionsProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={style} markerEnd={markerEnd} />
      {data?.domainEdge && (data.onConfirm || data.onReject) && (
        <EdgeLabelRenderer>
          <div
            // nodrag/nopan: React Flow's documented classNames for
            // interactive EdgeLabelRenderer content — without them, a
            // click on a pill is captured by the pane's own drag/pan
            // handling instead of reaching the button.
            className="nodrag nopan"
            data-testid={`pkg-edge-actions-${id}`}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              display: 'flex',
              gap: 4,
            }}
          >
            {data.onConfirm && (
              <button
                type="button"
                aria-label="Confirm AI-inferred edge"
                data-testid={`pkg-edge-confirm-${id}`}
                onClick={() => data.onConfirm?.(data.domainEdge)}
              >
                ✓
              </button>
            )}
            {data.onReject && (
              <button
                type="button"
                aria-label="Reject AI-inferred edge"
                data-testid={`pkg-edge-reject-${id}`}
                onClick={() => data.onReject?.(data.domainEdge)}
              >
                ✕
              </button>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
